import { useEffect, useState } from 'react';
import { Eye, Phone, Play, RefreshCw, Clock, Settings, Plus, FileText } from 'lucide-react';
import { me, api } from '@/api';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

function StatCard({ icon: Icon, label, value, color, bgColor }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div>
          <div className="text-3xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-500">{label}</div>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, description, onClick, gradient }) {
  return (
    <button 
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg transition-all hover:scale-[1.02] group"
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${gradient}`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-[#5848FF] transition-colors">
        {title}
      </h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </button>
  );
}

export default function ClientDashboard(){
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ totalViews: 0, totalContacts: 0, activeRequests: 0 });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const nav = useNavigate();

  const loadData = async () => {
    try {
      const profileRes = await me();
      setProfile(profileRes.data);
      
      const reqRes = await api.get('/requests/mine');
      const requests = reqRes.data || [];
      
      let totalViews = 0;
      let totalContacts = 0;
      let activeRequests = 0;
      
      requests.forEach(req => {
        totalViews += req.viewsCount || 0;
        totalContacts += req.contactsCount || 0;
        if (req.status === 'open') activeRequests++;
      });
      
      setStats({ totalViews, totalContacts, activeRequests });
      setRecentRequests(requests.slice(0, 3));
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" />
            </Link>
            
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => nav('/cliente/impostazioni')}
                className="text-slate-600"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
                className="text-slate-600"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Ciao, {profile?.name || 'Utente'}! 👋
            </h1>
            <p className="text-slate-500 mt-1">Ecco il riepilogo delle tue richieste</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              className="rounded-xl"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button 
              onClick={() => nav('/cliente/nuova')}
              className="rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white shadow-lg shadow-indigo-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Richiesta
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard 
            icon={Play} 
            label="Richieste Attive" 
            value={stats.activeRequests}
            color="text-indigo-600"
            bgColor="bg-indigo-100"
          />
          <StatCard 
            icon={Eye} 
            label="Visualizzazioni Totali" 
            value={stats.totalViews}
            color="text-blue-600"
            bgColor="bg-blue-100"
          />
          <StatCard 
            icon={Phone} 
            label="Contatti Ricevuti" 
            value={stats.totalContacts}
            color="text-green-600"
            bgColor="bg-green-100"
          />
        </div>

        {/* Actions Grid */}
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Azioni Rapide</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <ActionCard 
            icon={Plus}
            title="Nuova Richiesta"
            description="Pubblica una nuova richiesta di servizio"
            onClick={() => nav('/cliente/nuova')}
            gradient="bg-gradient-to-br from-emerald-500 to-green-600"
          />
          <ActionCard 
            icon={FileText}
            title="Le mie Richieste"
            description="Vedi tutte le tue richieste e chi ti ha contattato"
            onClick={() => nav('/cliente/richieste')}
            gradient="bg-gradient-to-br from-indigo-500 to-purple-600"
          />
          <ActionCard 
            icon={Settings}
            title="Impostazioni"
            description="Modifica il tuo profilo e preferenze"
            onClick={() => nav('/cliente/impostazioni')}
            gradient="bg-gradient-to-br from-slate-500 to-slate-700"
          />
        </div>

        {/* Recent Requests */}
        {recentRequests.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">Richieste Recenti</h2>
              <Button 
                variant="link" 
                onClick={() => nav('/cliente/richieste')}
                className="text-[#5848FF]"
              >
                Vedi tutte →
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentRequests.map(req => (
                <div key={req.id} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 truncate flex-1">
                      {req.title || 'Richiesta'}
                    </h3>
                    <span className={`ml-2 px-2 py-1 rounded-full text-[10px] font-semibold ${
                      req.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {req.status === 'open' ? 'Attiva' : 'Chiusa'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">{req.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-blue-600">
                      <Eye className="w-4 h-4" /> {req.viewsCount || 0}
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <Phone className="w-4 h-4" /> {req.contactsCount || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
